/**
 * Shared SEO metadata.
 *
 * The site answers on several hostnames (the apex domain, the `www` domain and
 * the Vercel deployment URL), so every indexable page states its canonical
 * `www` URL explicitly. Without it search engines treat those hostnames as
 * separate copies of the same content.
 */

export const SITE_URL = "https://www.altepapierfabrik.eu";
export const SITE_NAME = "Alte Papierfabrik Wuppertal";

const DEFAULT_OG_IMAGE = "/images/og/default.jpg";

/** Every card image in `public/images/og` is rendered at this size. */
const OG_IMAGE_WIDTH = "1200";
const OG_IMAGE_HEIGHT = "630";

const absoluteUrl = (pathname: string) => new URL(pathname, SITE_URL).href;

type SeoOptions = {
  title: string;
  description: string;
  /** Canonical path of the page, e.g. `/angebote/bowling`. */
  path: string;
  /** Share image path, relative to the site root. */
  image?: string;
  imageAlt?: string;
};

/**
 * Builds the `head` entry for an indexable page: title, description, canonical
 * URL and the Open Graph and Twitter tags that drive link previews.
 */
export function seo({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  imageAlt = SITE_NAME,
}: SeoOptions) {
  const url = absoluteUrl(path);
  const imageUrl = absoluteUrl(image);

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:image", content: imageUrl },
      { property: "og:image:width", content: OG_IMAGE_WIDTH },
      { property: "og:image:height", content: OG_IMAGE_HEIGHT },
      { property: "og:image:alt", content: imageAlt },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: imageUrl },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

/**
 * Site-wide tags for the root route. `HeadContent` resolves duplicates by
 * `name`/`property` with the deepest match winning, so anything here acts as a
 * default that `seo()` overrides per page.
 *
 * The canonical link deliberately lives on the individual routes instead: link
 * tags are concatenated rather than deduplicated, so a root-level canonical
 * would emit a second, always-wrong one on every page.
 */
export const siteMeta = [
  { title: SITE_NAME },
  {
    name: "description",
    content:
      "Alte Papierfabrik Wuppertal – Bowling und 3D-Schwarzlicht Minigolf. Jetzt online buchen!",
  },
  { property: "og:type", content: "website" },
  { property: "og:site_name", content: SITE_NAME },
  { property: "og:locale", content: "de_DE" },
  { property: "og:image", content: absoluteUrl(DEFAULT_OG_IMAGE) },
  { property: "og:image:width", content: OG_IMAGE_WIDTH },
  { property: "og:image:height", content: OG_IMAGE_HEIGHT },
  { property: "og:image:alt", content: SITE_NAME },
  { name: "twitter:card", content: "summary_large_image" },
];
